'use client';

import { Suspense } from 'react';
import { usePathname } from 'next/navigation';
import {
    Home, Trophy, Store, Clapperboard, Gift,
    ShieldCheck, Target, Dices, Menu, Info, Newspaper, Ticket, Users,
} from 'lucide-react';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '@/app/hooks/context/AuthContext';
import { Sites } from '@/types/enums';
import AuthButtons from '@/app/components/Navbar/AuthButtons';
import StakeLogo from '@/app/components/Navbar/StakeLogo';
import WalletInfoModal from '@/app/components/Navbar/WalletInfoModal';
import ProfileModal from '@/app/components/Navbar/Profile/ProfileModal';
import ExchangeModal from '@/app/components/ExchangeModal';
import WalletDropdown from '@/app/components/Navbar/WalletDropdown';
import GuestWalletDropdown from '@/app/components/Navbar/GuestWalletDropdown';
import TopBanner from '@/app/components/Navbar/TopBanner';
import LeftSidebar from '@/app/components/Navbar/LeftSidebar';
import { ADMIN_TABS } from '@/app/admin/tabs';

export default function Navbar() {
    const [sidebarOpen, setSidebarOpen] = useState<boolean>(() => {
        if (typeof window === 'undefined') return false;
        // Mobile keeps its hidden-by-default drawer; desktop now defaults to
        // expanded so the menu's contents are discoverable without a click,
        // unless the user has explicitly collapsed it before.
        if (window.innerWidth < 1024) return false;
        return localStorage.getItem('sidebar_open') !== '0';
    });
    const [showProfileModal, setShowProfileModal] = useState(false);
    const [showWalletInfo, setShowWalletInfo] = useState(false);
    const [showExchangeModal, setShowExchangeModal] = useState(false);
    const { user, loading } = useAuth();
    const pathname = usePathname();

    const isAdminSection = pathname.startsWith('/admin');

    const navLinks = useMemo(() => [
        { name: 'Home',        href: '/',                          icon: Home },
        { name: 'Leaderboards', href: `/leaderboard/${Sites.STAKE}`, icon: Trophy },
        { name: 'SokoPlay',    href: '/sokoplay',                  icon: Dices, label: 'New' },
        { name: 'Stake',       href: '/stakelevels',               icon: StakeLogo },
        { name: 'Store',       href: '/store',                     icon: Store },
        // Group — no page of its own, expands into its children in the sidebar.
        { name: 'Community',   href: '#community',                 icon: Users, children: [
            { name: 'Raffles', href: '/raffles', icon: Ticket, label: 'New' },
            { name: 'News',    href: '/news',    icon: Newspaper },
            { name: 'Earn',    href: '/clips',   icon: Clapperboard },
        ] },
        { name: 'Bonuses',     href: '/bonuses',                   icon: Gift },
        { name: 'Challenges',  href: '/challenges',                icon: Target },
        // About page temporarily disabled — the route also redirects home (app/about/page.tsx)
        // { name: 'About',       href: '/about',                     icon: Info },
        { name: 'Admin',       href: '/admin',                     icon: ShieldCheck, requiresAdmin: true },
    ], []);

    const defaultFilteredLinks = useMemo(() => navLinks.filter(link => {
        if (link.requiresAdmin && (!user || !['admin', 'mod'].includes(user.role))) return false;
        if ((link as { requiresKick?: boolean }).requiresKick && (!user || user.provider !== 'kick')) return false;
        return true;
    }), [navLinks, user]);

    // Inside /admin: swap the site nav for Home + Admin + the admin sections the role can see
    const adminSectionLinks = useMemo(() => [
        { name: 'Home',  href: '/',       icon: Home },
        { name: 'Admin', href: '/admin',  icon: ShieldCheck },
        ...ADMIN_TABS
            .filter(t => user?.role && t.roles.includes(user.role as 'admin' | 'mod'))
            .map(t => ({ name: t.label, href: `/admin?tab=${t.id}`, icon: t.icon })),
    ], [user]);

    const filteredLinks = isAdminSection ? adminSectionLinks : defaultFilteredLinks;

    // Close sidebar on mobile when navigating (desktop keeps its state)
    useEffect(() => {
        if (window.innerWidth < 1024) setSidebarOpen(false);
    }, [pathname]);

    // Escape closes everything
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key !== 'Escape') return;
            setShowProfileModal(false);
            setShowWalletInfo(false);
            setShowExchangeModal(false);
            setSidebarOpen(false);
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, []);

    const handleToggleSidebar = useCallback(() => {
        setSidebarOpen(prev => {
            const next = !prev;
            localStorage.setItem('sidebar_open', next ? '1' : '0');
            return next;
        });
    }, []);

    const handleCloseSidebar = useCallback(() => {
        setSidebarOpen(false);
        localStorage.setItem('sidebar_open', '0');
    }, []);
    const handleCloseProfile   = useCallback(() => setShowProfileModal(false), []);
    const handleCloseWallet    = useCallback(() => setShowWalletInfo(false), []);
    const handleOpenWallet     = useCallback(() => setShowWalletInfo(true), []);
    const handleOpenExchange   = useCallback(() => setShowExchangeModal(true), []);
    const handleCloseExchange  = useCallback(() => setShowExchangeModal(false), []);

    const isKickUser = user?.provider === 'kick';

    if (pathname?.startsWith('/raffle-overlay')) return null;

    return (
        <>
            <Suspense fallback={null}>
                <LeftSidebar
                    links={filteredLinks}
                    isOpen={sidebarOpen}
                    onToggle={handleToggleSidebar}
                    onClose={handleCloseSidebar}
                    variant={isAdminSection ? 'admin' : 'default'}
                />
            </Suspense>

            {/* Navbar — on lg+ starts after the 64px sidebar rail */}
            <nav
                id="site-navbar"
                suppressHydrationWarning
                className="font-bold fixed top-0 right-0 z-40 backdrop-blur shadow-sm border-b border-green-200/20 left-0 lg:left-16"
            >
                <div className="h-16 flex items-center justify-between px-4 sm:px-6">

                    {/* Left: mobile hamburger (only visible on mobile; desktop uses sidebar toggle) */}
                    <div className="lg:hidden">
                        <button
                            onClick={handleToggleSidebar}
                            className="p-2 -ml-2 text-gray-400 hover:text-gray-100 rounded-lg hover:bg-gray-800/50 transition-colors"
                            aria-label="Toggle menu"
                        >
                            <Menu size={22} />
                        </button>
                    </div>

                    {/* Spacer so center is truly centered on desktop (mirrors the auth side width) */}
                    <div className="hidden lg:block w-10" />

                    {/* Center: Wallet */}
                    <div className="flex-1 flex justify-center">
                        {!isAdminSection && !loading && (
                            isKickUser && user?.points !== undefined ? (
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={handleOpenWallet}
                                        aria-label="Wallet information"
                                        className="rounded-xl p-2 hover:bg-gray-800/50 transition-colors"
                                    >
                                        <Info size={18} className="text-gray-200" />
                                    </button>
                                    <WalletDropdown user={user} onOpenExchange={handleOpenExchange} />
                                </div>
                            ) : (
                                !user && <GuestWalletDropdown />
                            )
                        )}
                    </div>

                    {/* Right: auth buttons */}
                    <div className="flex items-center">
                        {!loading && (
                            <AuthButtons
                                user={user}
                                setShowProfileModal={setShowProfileModal}
                            />
                        )}
                    </div>
                </div>
            </nav>

            <TopBanner />

            {showWalletInfo  && <WalletInfoModal onClose={handleCloseWallet} />}
            {showProfileModal && user && <ProfileModal user={user} onClose={handleCloseProfile} />}
            {showExchangeModal && <ExchangeModal onClose={handleCloseExchange} />}
        </>
    );
}
